import { useTheme } from '@mui/material';
import { portalPaths, UrlSegments } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import {
  EntityDetachedPanel,
  EntityMobilePeek,
  EntityPeekSlot,
  useEntityRecordClick,
} from 'src/components/EntitySidePanel';
import { MetaTags } from 'src/components/MetaTags/meta-tags';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { Address, useAddressRecord } from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { BaseSelectedView } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { CreateButton } from 'src/pages/CustomViews/custom-views-selected-view/components/create-button';
import { useSelectedEntityParams } from 'src/pages/CustomViews/custom-views-selected-view/hooks/use-selected-entity-params';
import { SiteSidePanel } from 'src/pages/Sites/SidePanel/side-panel';
import { TabType, useCustomView, useOnEsc } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing/use-navigate-route';
import { useScreenDimensions } from 'src/utils/system';

// Mobile peek: the panel is a bare card column, so the host owns the scroller and
// the sticky tools slot (mirrors the mobile branch of SiteView/site-expanded.tsx).
const SiteMobilePanel = ({ site, onClose }: { site: Address; onClose: () => void }) => (
  <EntityMobilePeek>
    {(toolsSlot) => <SiteSidePanel site={site} onClose={onClose} toolsSlot={toolsSlot} />}
  </EntityMobilePeek>
);

interface SiteDetachedPanelProps {
  site: Address;
  onClose: () => void;
  customViewId?: string | null;
}

const SiteDetachedPanel = ({ site, onClose, customViewId }: SiteDetachedPanelProps) => {
  const { navigate } = useNavigateRoute();
  const theme = useTheme();

  return (
    <EntityDetachedPanel
      icon="home"
      entityColor={theme.palette.custom.OV.entity.site}
      title={site.fullAddress}
      detailsCardTestId="site-details-card"
      resetKey={site.addressId}
      closeTooltip="Close Site"
      menu={<SiteMenu address={site} />}
      onOpenFullPage={() => {
        navigate(portalPaths.site.full(site.id), { state: { customViewId } });
      }}
      onClose={onClose}
    >
      {(toolsSlot) => <SiteSidePanel site={site} onClose={onClose} toolsSlot={toolsSlot} />}
    </EntityDetachedPanel>
  );
};

export const SiteSelectedView = () => {
  const { navigate } = useNavigateRoute();
  const { isMobile } = useScreenDimensions();
  const [selectedRecordId, setSelectedRecordId] = useState<Address['id'] | null>(null);

  const customViewId = useCustomView(EntityNames.Site)?.customViewId;

  const { address: selectedRecord } = useAddressRecord({ addressId: selectedRecordId || '' });

  const {
    record: address,
    recordId: addressId,
    openRecord,
    closeRecord,
    customView,
    viewId,
    selectedCustomViewId,
  } = useSelectedEntityParams({ entityName: EntityNames.Site });

  const selectedAddress = useMemo(
    () => (addressId ? ((address as Address) ?? selectedRecord) : undefined),
    [addressId, address, selectedRecord],
  );

  const theme = useTheme();

  const onPanelClose = useCallback(() => {
    closeRecord();
    setSelectedRecordId(null);
  }, [closeRecord]);

  useOnEsc(onPanelClose);

  const onRecordClick = useEntityRecordClick<Address>({
    customViewId,
    buildTabEntity: (site) => ({ entity: site, type: TabType.Site }),
    onOpenFullPage: (siteId) => {
      navigate(portalPaths.site.full(siteId), { state: { customViewId } });
    },
    onOpenRecord: (siteId) => openRecord(siteId),
  });

  return (
    <MetaTags title={selectedAddress?.fullAddress || customView?.name || 'Sites'}>
      <BaseSelectedView<Address>
        title={selectedCustomViewId === UrlSegments.view.all ? 'All sites' : ''}
        entity={EntityNames.Site}
        viewId={viewId}
        selectedRecord={selectedAddress}
        hasDetachedPanel={!isMobile}
        onRecordClick={onRecordClick}
        onRecordOpenFullPage={(siteId) => {
          navigate(portalPaths.site.full(siteId), { state: { customViewId } });
        }}
        onRecordClose={() => {
          closeRecord();
          setSelectedRecordId(null);
        }}
        sx={{
          [theme.breakpoints.up(0)]: {
            maxWidth: '100%',
          },
        }}
        slots={{
          rightPanel: !!selectedAddress && (
            <EntityPeekSlot open={!!selectedAddress} isMobile={isMobile}>
              {isMobile ? (
                <SiteMobilePanel site={selectedAddress} onClose={onPanelClose} />
              ) : (
                <SiteDetachedPanel
                  site={selectedAddress}
                  onClose={onPanelClose}
                  customViewId={customViewId}
                />
              )}
            </EntityPeekSlot>
          ),
          createButton: <CreateButton icon="add_home_work" />,
        }}
      />
    </MetaTags>
  );
};
